import { useQuery } from "@apollo/client";
import moment from "moment";
import { useMemo, useState } from "react";
import {
  GetComplianceProgramTimelineQuery,
  GetComplianceProgramTimelineQueryVariables,
  TimelineEntryKind,
  TimelineSkipReason,
} from "types/graphql-schema";

import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";

import getComplianceProgramTimelineQuery from "../graphql/get-compliance-program-timeline-query";
import { useProgramsSection } from "../programs-section-context";

type TimelineRow = NonNullable<
  NonNullable<
    GetComplianceProgramTimelineQuery["complianceProgram"]
  >["timeline"]
>[number];

/**
 * The rows that send one message. Kickoff is deliberately not one of them: it is
 * the merge of every message that goes out that day, and those messages are listed
 * under the same date, so counting them twice on one day reads as two sends.
 */
const sendingKinds: TimelineEntryKind[] = [
  TimelineEntryKind.Notification,
  TimelineEntryKind.Reminder,
];

/** Why one person hears nothing. */
const skipReasonLabels: Record<TimelineSkipReason, string> = {
  [TimelineSkipReason.NoManager]: "no manager",
  [TimelineSkipReason.OptedOut]: "opted out",
  [TimelineSkipReason.NotApplicable]: "not taking part",
  [TimelineSkipReason.ChatDisabled]: "no chat account",
  [TimelineSkipReason.AlreadyReminded]: "already reminded",
  [TimelineSkipReason.NotLocalSendHour]: "outside their send hour",
};

const channelLabels: Record<string, string> = {
  email: "email",
  slack: "Slack",
  teams: "Teams",
};

/**
 * How many people a row reaches, in words. Only a sending row has an audience, and
 * a row whose recipients nobody can know yet says so rather than reporting nobody.
 */
const audience = (row: TimelineRow) => {
  if (!sendingKinds.includes(row.kind)) {
    return null;
  }
  if (!row.recipientsKnown) {
    return "recipients not known yet";
  }
  return `${row.recipientCount} ${
    row.recipientCount === 1 ? "person" : "people"
  }`;
};

/** One reason when every skipped person shares it, which is the usual case. */
const sharedSkipReason = (row: TimelineRow) => {
  const reasons = new Set(row.skipped.map((skip) => skip.reason));
  return reasons.size === 1
    ? skipReasonLabels[row.skipped[0].reason]
    : "several reasons";
};

const TimelineItem = ({ row }: { row: TimelineRow }) => {
  const [expanded, setExpanded] = useState(false);
  const sends = sendingKinds.includes(row.kind);
  const details = [
    audience(row),
    row.note,
    row.channels.map((channel) => channelLabels[channel] ?? channel).join(", "),
  ].filter(Boolean);

  return (
    // A row whose day has passed dims as a whole. Dimming only its label left the
    // badge, the counts and the skipped control at full strength, so half the row
    // still read as something about to happen.
    <div
      className={classNames("flex flex-col gap-1", row.isPast && "opacity-60")}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {sends && (
          <span className="shrink-0 border border-blue-500 px-1.5 text-[10px] font-medium uppercase tracking-wide text-blue-600">
            {row.kind === TimelineEntryKind.Reminder ? "Reminder" : "Sends"}
          </span>
        )}
        <span className="text-sm">{row.label}</span>
        {details.length > 0 && (
          <span className="text-xs text-gray-500">{details.join(" · ")}</span>
        )}
        {sends && row.skippedCount > 0 && (
          <button
            type="button"
            className="shrink-0 bg-amber-50 px-1.5 text-xs text-amber-800 underline"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {row.skippedCount} skipped · {sharedSkipReason(row)}
          </button>
        )}
      </div>
      {expanded && (
        <ul className="pl-1 text-xs text-gray-500">
          {row.skipped.map((skip, index) => (
            <li key={`${skip.user?.id ?? index}-${skip.reason}`}>
              {skip.user?.name ?? "Somebody who has left"} —{" "}
              {skipReasonLabels[skip.reason] ?? skip.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** The rows that land on one day, in the order the plan gives them. */
type TimelineDay = { date: string; rows: TimelineRow[] };

const groupByDate = (rows: readonly TimelineRow[]): TimelineDay[] => {
  const days: TimelineDay[] = [];
  rows.forEach((row) => {
    const current = days[days.length - 1];
    if (current && current.date === row.date) {
      current.rows.push(row);
    } else {
      days.push({ date: row.date, rows: [row] });
    }
  });
  return days;
};

/**
 * What the day is about, in a few words. Kickoff and a step falling due are the
 * two things an admin looks for in a calendar, so they name the day when present.
 */
const dayHeadline = (day: TimelineDay) => {
  if (day.rows.some((row) => row.kind === TimelineEntryKind.Kickoff)) {
    return "Kickoff";
  }
  const due = day.rows.find((row) => row.kind === TimelineEntryKind.StepDue);
  return due ? due.label : day.rows[0].label;
};

const ComplianceProgramTimelineDay = ({ day }: { day: TimelineDay }) => (
  <div className="flex flex-col border-b border-gray-200 last:border-b-0 sm:flex-row">
    <div className="shrink-0 bg-gray-50 px-4 py-4 text-sm font-medium sm:w-44 sm:border-r sm:border-gray-200">
      {moment(day.date).format("MMM D, YYYY")}
      <span className="mt-0.5 block text-[11px] font-normal uppercase tracking-wide text-gray-500">
        {dayHeadline(day)}
      </span>
    </div>
    <div className="flex flex-1 flex-col gap-2.5 px-4 py-4">
      {day.rows.map((row, index) => (
        <TimelineItem key={`${row.kind}-${row.label}-${index}`} row={row} />
      ))}
    </div>
  </div>
);

const ComplianceProgramTimeline = ({
  complianceProgramId,
}: {
  complianceProgramId: number;
}) => {
  const { noun } = useProgramsSection();
  const { data, loading, error } = useQuery<
    GetComplianceProgramTimelineQuery,
    GetComplianceProgramTimelineQueryVariables
  >(getComplianceProgramTimelineQuery, {
    fetchPolicy: "cache-and-network",
    variables: { complianceProgramId },
  });

  const days = useMemo(
    () => groupByDate(data?.complianceProgram?.timeline ?? []),
    [data],
  );

  return (
    <div className="flex flex-col gap-4" aria-label={`${noun} timeline`}>
      <div className="text-sm text-gray-500">
        What this {noun.toLowerCase()} will do, and who each message reaches.
      </div>

      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        {days.map((day) => (
          <ComplianceProgramTimelineDay key={day.date} day={day} />
        ))}
        {days.length === 0 && loading && (
          <div className="p-4 text-center">
            <Loading size="5" />
          </div>
        )}
        {/* A failed request must not read as "this does nothing". */}
        {days.length === 0 && !loading && error && (
          <div className="p-4 text-center text-sm text-gray-500">
            This timeline could not be loaded. Reload the page to try again.
          </div>
        )}
        {days.length === 0 && !loading && !error && (
          <div className="p-4 text-center text-sm text-gray-500">
            Nothing is scheduled yet. Set a start date and a due date to see the
            timeline.
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceProgramTimeline;
